import * as cn from "classnames";
import * as React from "react";
import { ChartDataResponse, MetricInfo, DimensionInfo, MetricLabels } from "@api/common";
import { ReportLabel } from "../ReportLabel";
import { LegendItem } from "../LegendItem";
import { MetricsTable } from "./MetricsTable";
import "./ChartWithTable.css";

export interface ChartWithTableProps {
    data: ChartDataResponse;
    metrics: MetricInfo[];
    dimensions: DimensionInfo[];
    title: string;
    subtitle?: string;

    children?: (metric: MetricInfo) => React.ReactChild | React.ReactChild[];

    className?: string;
}

export interface ChartWithTableState {
    selectedMetric: MetricInfo;
}

export class ChartWithTable extends React.Component<ChartWithTableProps, ChartWithTableState> {
    constructor(props: ChartWithTableProps, context?: any) {
        super(props, context);

        const metrics = props.metrics || [];

        this.state = {
            selectedMetric: metrics.find(m => m.isDefault) || metrics[0]
        };
    }

    render() {
        const { data, metrics, dimensions, title, subtitle, className, children } = this.props;
        const { selectedMetric } = this.state;

        const tableMetrics = metrics.filter(m => m.showInTable);

        const nullValue = getNullDimensionValue(data, selectedMetric);

        return (
            <div className={cn("ChartWithTable", className)}>
                <div className="ChartWithTable__header">
                    <ReportLabel className="ChartWithTable__label" title={title} subtitle={subtitle} />
                    <div className="legend">
                        {metrics.filter(m => m.showInLegend).map(metric => (
                            <LegendItem
                                color={metric.color}
                                checked={metric.metric === selectedMetric.metric}
                                onChange={() => this.setState({ selectedMetric: metric })}
                                key={metric.metric}
                            >
                                {MetricLabels.tableColumnHeader(metric)}
                            </LegendItem>
                        ))}
                    </div>
                </div>
                <div className="ChartWithTable__content">
                    {children && <div className="ChartWithTable__chart">{children(selectedMetric)}</div>}
                    <div className="ChartWithTable__table">
                        <MetricsTable
                            data={data}
                            metrics={tableMetrics}
                            dimensions={dimensions}
                            percentMetric={selectedMetric}
                        />
                    </div>
                </div>
                {(nullValue || nullValue === 0) && (
                    <div className="ChartWithTable__footer">{`*${nullValue} of total devices were not identified`}</div>
                )}
            </div>
        );
    }
}

function getNullDimensionValue(data: ChartDataResponse, metric: MetricInfo): number | undefined {
    const metricData = data.metrics.find(m => m.metric === metric.metric);
    if (!metricData) {
        return undefined;
    }

    const nullValue = metricData.data.find(d => d["dimension-values"][0] === null);

    if (!nullValue) {
        return undefined;
    }

    return nullValue["metric-value"];
}
